import { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";

/**
 * MYID-D4 — Offline banner.
 *
 * Sticky strip across the top of the viewport when navigator.onLine is
 * false. Doesn't block interaction; persisted-query reads still serve
 * cached values, writes will fail loudly via the existing toast paths.
 *
 * No i18n strings yet — drop them in during MYID-D5 (i18n bootstrap)
 * by wrapping the visible text in `t('errors.offline.banner')` etc.
 *
 * Pattern lifted from karePortal apps/next/. Cheap subscription to
 * window.online/offline events; no polling.
 */
export default function OfflineBanner() {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  if (isOnline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-50 flex items-center justify-center gap-2 bg-amber-100 px-4 py-1.5 text-sm text-amber-900 shadow-sm dark:bg-amber-900/30 dark:text-amber-200"
    >
      <WifiOff className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        You're offline. Showing your last-known data — changes will sync when you reconnect.
      </span>
    </div>
  );
}
